import { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  House,
  ChartBar,
  Buildings,
  Users,
  Storefront,
  GraduationCap,
  CreditCard,
  IdentificationCard,
  ChartLine,
  Gear,
  ClipboardText,
  CaretLeft,
  CaretRight,
  CaretDown,
  SignOut,
  UserCircleGear,
  Wallet,
  Coins,
  ArrowsClockwise,
  Receipt,
  SquaresFour,
  ChatCircleText,
  BookOpenText,
  DeviceMobile,
  Headset,
  UsersThree,
  MapTrifold,
  ChartPieSlice,
  ClockCounterClockwise,
  Ticket,
  Gauge,
  QrCode,
  X as XIcon,
} from '@phosphor-icons/react';

const menuItems = [
  { path: '/dashboard',           label: 'Dashboard',           icon: House,              roles: ['entity_admin', 'back_office', 'staff', 'ambassador'],       module: 'dashboard' },
  { path: '/super-dashboard',     label: 'Super Dashboard',     icon: ChartBar,           roles: ['super_admin'] },
  {
    key: 'modules',
    label: 'Modules',
    icon: SquaresFour,
    roles: ['super_admin', 'entity_admin', 'back_office', 'staff', 'ambassador'],
    children: [
      {
        key: 'subscribers-group',
        label: 'Subscribers',
        icon: Users,
        roles: ['super_admin', 'entity_admin', 'back_office', 'staff', 'ambassador'],
        // Visibility driven by whether ANY child sub-module is enabled.
        children: [
          { path: '/subscribers',   label: 'Manage Subscribers', icon: Users,           roles: ['super_admin', 'entity_admin', 'back_office', 'staff', 'ambassador'], module: 'subscribers' },
          { path: '/card-renewals', label: 'Card Renewals',      icon: ArrowsClockwise, roles: ['super_admin', 'entity_admin', 'back_office', 'staff', 'ambassador'], module: 'subscribers' },
        ],
      },
      {
        key: 'cashiers-group',
        label: 'Cashiers',
        icon: UserCircleGear,
        roles: ['super_admin', 'entity_admin', 'back_office'],
        // No `module` on the parent — visibility is driven by whether ANY
        // child sub-module is enabled for the entity (see visibleChildren).
        children: [
          { path: '/cashiers',                label: 'Manage Cashiers', icon: UserCircleGear, roles: ['super_admin', 'entity_admin', 'back_office'], module: 'cashiers' },
          { path: '/cashiers/float-logs',     label: 'Float Logs',      icon: Wallet,         roles: ['super_admin', 'entity_admin', 'back_office'], module: 'float_logs' },
          { path: '/cashiers/payment-orders', label: 'Payment Orders',  icon: Receipt,        roles: ['super_admin', 'entity_admin', 'back_office'], module: 'payment_orders' },
        ],
      },
      { path: '/campuses',            label: 'Campuses',            icon: GraduationCap,      roles: ['super_admin', 'entity_admin', 'back_office', 'staff'],                     module: 'campuses' },
      { path: '/stores',              label: 'Stores',              icon: Storefront,         roles: ['super_admin', 'entity_admin', 'back_office', 'staff'],                     module: 'stores' },
      {
        key: 'wallet-transactions',
        label: 'Wallet Transactions',
        icon: Receipt,
        roles: ['super_admin', 'entity_admin', 'back_office', 'staff'],
        module: 'wallet_transactions',
        children: [
          { path: '/wallet-transactions',                label: 'Transactions', icon: Receipt, roles: ['super_admin', 'entity_admin', 'back_office', 'staff'] },
          { path: '/wallet-transactions/inter-wallets',  label: 'Inter Wallets', icon: Wallet,  roles: ['super_admin', 'entity_admin'], module: 'inter_wallets' },
        ],
      },
      { path: '/messaging',           label: 'Messaging',           icon: ChatCircleText,     roles: ['super_admin', 'entity_admin', 'back_office'],                              module: 'messaging' },
      { path: '/cx',                  label: 'Customer Experience', icon: Headset,            roles: ['super_admin', 'entity_admin'],                                             module: 'customer_experience' },
      { path: '/customer-hub',        label: 'Customer Connect',    icon: QrCode,             roles: ['super_admin', 'entity_admin'],                                             module: 'customer_connect' },
      { path: '/card-centre',         label: 'Card Centre',         icon: IdentificationCard, roles: ['super_admin', 'entity_admin', 'back_office', 'staff', 'ambassador'],      module: 'card_centre' },
      { path: '/analytics',           label: 'Analytics',           icon: ChartLine,          roles: ['super_admin', 'entity_admin', 'back_office'],                              module: 'analytics' },
    ],
  },
  { path: '/entities',            label: 'Entities',            icon: Buildings,          roles: ['super_admin'] },
  {
    key: 'mobile-apps',
    label: 'Mobile Apps',
    icon: DeviceMobile,
    roles: ['super_admin'],
    children: [
      { path: '/mobile-apps/ecoplus',  label: 'Ecoplus',  icon: DeviceMobile, roles: ['super_admin'] },
      { path: '/mobile-apps/ecofront', label: 'Ecofront', icon: DeviceMobile, roles: ['super_admin'] },
    ],
  },
  { path: '/float-ledger',        label: 'Float Ledger',        icon: Wallet,             roles: ['super_admin'] },
  { path: '/billing',             label: 'Billing',             icon: Coins,              roles: ['super_admin'] },
  { path: '/users',               label: 'Users',               icon: Users,              roles: ['super_admin', 'entity_admin'],                              module: 'users' },
  { path: '/configurations',      label: 'Configurations',      icon: Gear,               roles: ['super_admin'] },
  { path: '/audit-trail',         label: 'Audit Trail',         icon: ClipboardText,      roles: ['super_admin'] },
  { path: '/documentation',       label: 'Documentation',       icon: BookOpenText,       roles: ['super_admin'] },
];

export default function Sidebar({ collapsed = false, onToggle, mobileOpen = false, onMobileClose }) {
  const setCollapsed = (val) => {
    // Support boolean OR functional updater style for parity with useState.
    const nextVal = typeof val === 'function' ? val(collapsed) : val;
    if (nextVal !== collapsed && typeof onToggle === 'function') onToggle();
  };
  const [openGroups, setOpenGroups] = useState({});
  const { user, logout } = useAuth();
  const location = useLocation();

  const hasRole = (roles) => roles.includes(user?.role);
  // Module gate: super_admin bypasses, everyone else must have the module
  // in their `enabled_modules` set. Items without a `module` key are
  // ungated (used by super-admin-only items that don't belong to the
  // entity catalogue).
  //
  // Defensive fallback: if `enabled_modules` is missing entirely from the
  // user object (e.g. stale browser cache from a build that predates the
  // modules feature), DON'T hide everything — degrade gracefully to
  // role-only filtering so the user can still navigate. The next /auth/me
  // call will populate the field correctly.
  const modulesKnown = Array.isArray(user?.enabled_modules);
  const enabledModules = new Set(user?.enabled_modules || []);
  const hasModule = (key) => !key
    || user?.role === 'super_admin'
    || !modulesKnown        // unknown set → don't gate
    || enabledModules.has(key);
  // For non-super-admin roles, flatten any module groups so children
  // appear directly in the sidebar instead of being nested under "Modules".
  // Super admins keep the grouped structure.
  const expandedMenu = user?.role === 'super_admin'
    ? menuItems
    : menuItems.flatMap((item) => (item.children ? item.children : [item]));

  const filteredMenu = expandedMenu
    .filter(item => hasRole(item.roles))
    .filter(item => hasModule(item.module));

  // Auto-open a group if current path matches one of its children (recursively).
  useEffect(() => {
    const updates = {};
    const walk = (items) => {
      items.forEach((it) => {
        if (!it.children) return;
        const directMatch = it.children.some(c => c.path === location.pathname);
        const nestedMatch = it.children.some(c => c.children && c.children.some(gc => gc.path === location.pathname));
        if (directMatch || nestedMatch) {
          updates[it.key] = true;
        }
        walk(it.children);
      });
    };
    walk(filteredMenu);
    if (Object.keys(updates).length) setOpenGroups(prev => ({ ...prev, ...updates }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const toggleGroup = (key) => setOpenGroups(prev => ({ ...prev, [key]: !prev[key] }));

  // Sidebar color. The notch is a single transparent SVG with one filled path.
  const SIDEBAR_BG = '#0A2540';
  const NOTCH = 40;

  // Concave notch that carves the sidebar's right edge around the active pill.
  // We extend the SVG 1px BACK into the sidebar to cover any anti-alias seam
  // between the SVG's blue and the parent sidebar's solid background.
  const Notch = ({ position }) => {
    const r = NOTCH;
    // sweep=0 → arc bulges toward the box origin (i.e., toward the pill).
    const d = position === 'top'
      ? `M 0 0 H ${r} A ${r} ${r} 0 0 0 0 ${r} Z`            // scoop carved at bottom-right
      : `M 0 0 H 0 V ${r} H ${r} A ${r} ${r} 0 0 0 0 0 Z`;   // scoop carved at top-right
    return (
      <svg
        aria-hidden="true"
        width={r + 1}
        height={r}
        viewBox={`0 0 ${r + 1} ${r}`}
        className="pointer-events-none absolute block"
        style={{
          right: 0,
          top: position === 'top' ? -r : 'auto',
          bottom: position === 'bottom' ? -r : 'auto',
          // Overlap 1px back into the sidebar to hide any anti-alias seam.
          marginRight: -1,
        }}
        data-testid={`sidebar-notch-${position}`}
      >
        <path d={d} fill={SIDEBAR_BG} shapeRendering="geometricPrecision" />
      </svg>
    );
  };

  return (
    <aside
      className={`fixed left-0 top-0 h-screen transition-all duration-300 z-40 flex flex-col ${
        collapsed ? 'w-16' : 'w-64'
      } ${
        // Mobile: slide-in drawer behavior. The aside is hidden off-canvas
        // by default and slides into view when mobileOpen=true. On md+ it
        // sits in place regardless of mobileOpen.
        mobileOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'
      }`}
      style={{ backgroundColor: SIDEBAR_BG }}
      data-testid="sidebar"
    >
      {/* Logo Section */}
      <div className="h-16 flex items-center justify-between px-5 border-b border-white/10">
        {!collapsed && (
          <div className="flex items-baseline gap-1.5">
            <img
              src="https://customer-assets.emergentagent.com/job_ecowater-cloud/artifacts/tzhqe5c4_ECOWATER_WORDMARK_LOGO.png"
              alt="Ecowater"
              className="h-6 w-auto object-contain brightness-0 invert"
            />
            <span className="text-[10px] font-bold tracking-[0.2em] text-white/80" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
              CLOUD
            </span>
          </div>
        )}
        {/* Mobile close button — only shown on < md */}
        <button
          type="button"
          onClick={onMobileClose}
          data-testid="sidebar-mobile-close"
          aria-label="Close menu"
          className="md:hidden p-1.5 rounded-md text-white/85 hover:bg-white/10"
        >
          <XIcon size={18} />
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto overflow-x-hidden py-4 pr-0">
        <ul className="space-y-2">
          {filteredMenu.map((item) => {
            const Icon = item.icon;

            // Group with children → dropdown
            if (item.children) {
              const visibleChildren = item.children
                .filter(c => hasRole(c.roles))
                .filter(c => hasModule(c.module));
              if (visibleChildren.length === 0) return null;
              const isOpen = !!openGroups[item.key];
              const groupActive = visibleChildren.some(c => c.path === location.pathname);

              return (
                <li key={item.key}>
                  <button
                    type="button"
                    onClick={() => {
                      if (collapsed) setCollapsed(false);
                      toggleGroup(item.key);
                    }}
                    data-testid={`nav-group-${item.key}`}
                    className={`w-full flex items-center gap-3 pl-5 pr-3 h-12 transition-all duration-200 mr-3 rounded-l-full ${
                      groupActive
                        ? 'text-white font-semibold'
                        : 'text-white/85 hover:bg-[#FFFFFF]/10 hover:text-white'
                    }`}
                    title={collapsed ? item.label : undefined}
                  >
                    <Icon size={20} weight={groupActive ? 'fill' : 'regular'} />
                    {!collapsed && (
                      <>
                        <span className="text-sm tracking-tight flex-1 text-left" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                          {item.label}
                        </span>
                        <CaretDown
                          size={14}
                          className={`transition-transform duration-200 ${isOpen ? 'rotate-0' : '-rotate-90'}`}
                        />
                      </>
                    )}
                  </button>

                  {!collapsed && isOpen && (
                    <ul className="space-y-0.5 pb-1" data-testid={`nav-group-${item.key}-list`}>
                      {visibleChildren.map(child => {
                        // Nested sub-group (e.g. Cashiers → Manage / Float Logs / Payment Orders)
                        if (child.children) {
                          const subVisible = child.children
                            .filter(c => hasRole(c.roles))
                            .filter(c => hasModule(c.module));
                          if (subVisible.length === 0) return null;
                          const SubIcon = child.icon;
                          const subOpen = !!openGroups[child.key];
                          const subActive = subVisible.some(c => c.path === location.pathname);
                          return (
                            <li key={child.key}>
                              <button
                                type="button"
                                onClick={() => toggleGroup(child.key)}
                                data-testid={`nav-subgroup-${child.key}`}
                                className={`w-full flex items-center gap-2.5 pl-12 pr-3 h-10 mr-3 rounded-l-full transition-colors ${
                                  subActive
                                    ? 'text-white font-semibold'
                                    : 'text-white/85 hover:bg-[#FFFFFF]/10 hover:text-white'
                                }`}
                              >
                                <SubIcon size={16} weight={subActive ? 'fill' : 'regular'} />
                                <span className="text-sm tracking-tight flex-1 text-left" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                                  {child.label}
                                </span>
                                <CaretDown
                                  size={12}
                                  className={`transition-transform duration-200 ${subOpen ? 'rotate-0' : '-rotate-90'}`}
                                />
                              </button>
                              {subOpen && (
                                <ul className="space-y-0.5 pb-1" data-testid={`nav-subgroup-${child.key}-list`}>
                                  {subVisible.map(sc => {
                                    const ScIcon = sc.icon;
                                    const scActive = location.pathname === sc.path;
                                    return (
                                      <li key={sc.path} className="relative">
                                        {scActive && <><Notch position="top" /><Notch position="bottom" /></>}
                                        <NavLink
                                          to={sc.path}
                                          data-testid={`nav-${sc.path.slice(1).replace(/\//g, '-')}`}
                                          onClick={onMobileClose}
                                          className={`relative flex items-center gap-2.5 transition-all duration-200 ${
                                            scActive
                                              ? 'pl-[4.75rem] pr-6 h-10 mr-[-16px] rounded-full bg-[#FFFFFF] text-[#0A2540] font-semibold shadow-[0_6px_18px_-6px_rgba(15,23,42,0.25)]'
                                              : 'pl-[4.75rem] pr-3 py-2 mr-3 rounded-md text-white/70 hover:bg-[#FFFFFF]/10 hover:text-white'
                                          }`}
                                        >
                                          <ScIcon size={14} weight={scActive ? 'fill' : 'regular'} />
                                          <span className="text-[13px] tracking-tight" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                                            {sc.label}
                                          </span>
                                        </NavLink>
                                      </li>
                                    );
                                  })}
                                </ul>
                              )}
                            </li>
                          );
                        }

                        const CIcon = child.icon;
                        const isActive = location.pathname === child.path;
                        return (
                          <li key={child.path} className="relative">
                            {isActive && <><Notch position="top" /><Notch position="bottom" /></>}
                            <NavLink
                              to={child.path}
                              data-testid={`nav-${child.path.slice(1).replace(/\//g, '-')}`}
                              onClick={onMobileClose}
                              className={`relative flex items-center gap-2.5 transition-all duration-200 ${
                                isActive
                                  ? 'pl-12 pr-6 h-12 mr-[-16px] rounded-full bg-[#FFFFFF] text-[#0A2540] font-semibold shadow-[0_6px_18px_-6px_rgba(15,23,42,0.25)]'
                                  : 'pl-12 pr-3 py-2 mr-3 rounded-md text-white/75 hover:bg-[#FFFFFF]/10 hover:text-white'
                              }`}
                            >
                              <CIcon size={16} weight={isActive ? 'fill' : 'regular'} />
                              <span className="text-sm tracking-tight" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                                {child.label}
                              </span>
                            </NavLink>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </li>
              );
            }

            // Flat item
            const isActive = location.pathname === item.path;
            return (
              <li key={item.path} className="relative">
                {isActive && <><Notch position="top" /><Notch position="bottom" /></>}
                <NavLink
                  to={item.path}
                  data-testid={`nav-${item.path.slice(1)}`}
                  onClick={onMobileClose}
                  className={`relative flex items-center gap-3 transition-all duration-200 ${
                    isActive
                      ? 'pl-5 pr-6 h-12 mr-[-16px] rounded-full bg-[#FFFFFF] text-[#0A2540] font-semibold shadow-[0_6px_18px_-6px_rgba(15,23,42,0.25)]'
                      : 'pl-5 pr-3 py-2.5 mr-3 rounded-l-full text-white/85 hover:bg-[#FFFFFF]/10 hover:text-white'
                  }`}
                  title={collapsed ? item.label : undefined}
                >
                  <Icon size={20} weight={isActive ? 'fill' : 'regular'} />
                  {!collapsed && (
                    <span className="text-sm tracking-tight" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                      {item.label}
                    </span>
                  )}
                </NavLink>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* User Section */}
      <div className="border-t border-white/15 p-4">
        {!collapsed && (
          <div className="mb-3">
            <p
              className="text-[10px] text-white/60 uppercase tracking-wider"
              style={{ fontFamily: 'JetBrains Mono, monospace' }}
            >
              {user?.role?.replace('_', ' ')}
            </p>
            <p
              className="text-sm text-white font-medium truncate"
              style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
            >
              {user?.fullname}
            </p>
          </div>
        )}
        <button
          onClick={logout}
          data-testid="logout-btn"
          className={`flex items-center gap-2 text-white/85 hover:bg-[#FFFFFF]/10 hover:text-white rounded-lg transition-colors ${
            collapsed ? 'p-2 justify-center w-full' : 'px-3 py-2'
          }`}
          title={collapsed ? 'Logout' : undefined}
        >
          <SignOut size={20} />
          {!collapsed && <span className="text-sm">Logout</span>}
        </button>
      </div>

      {/* Collapse Toggle (desktop only) */}
      <button
        onClick={() => setCollapsed(!collapsed)}
        data-testid="sidebar-toggle"
        className="hidden md:block absolute -right-3 top-20 bg-[#FFFFFF] border border-[#EDE6D8] rounded-full p-1 shadow-sm hover:bg-[#FCFAF6] transition-colors z-10"
      >
        {collapsed ? (
          <CaretRight size={16} className="text-[#0A2540]" />
        ) : (
          <CaretLeft size={16} className="text-[#0A2540]" />
        )}
      </button>
    </aside>
  );
}
